import {
  type FormUiFields,
  type FormUiWriteResult,
  type PageUpdatePreviewRow,
} from '@elementary/common-ui/components/AiChat';
import { WarehouseType } from '@elementary/common-ui/types';
import {
  capitalizeWords,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { UseFormReturn } from 'react-hook-form';
import { AddTestForm } from 'shared/types';
import {
  WizardMode,
  WizardResourceType,
  WizardSelectableTest,
  WizardStageInfo,
} from 'testManagement/types';
import {
  coerceWizardStringList,
  extractWizardSeverityFromYaml,
  getUnselectableWizardResourcesReason,
  getWizardFieldLockReason,
  getWizardWriteFieldError,
  normalizeWizardWriteFieldValue,
  resolveWizardTestSelection,
  setWizardFormField,
} from 'testManagement/utils/utils';
import { normalizeWizardTimestampFieldWrite } from 'testManagement/utils/wizardTimestampFields';
import {
  WIZARD_COMMON_CONFIG_FIELD_NAMES,
  WIZARD_CONFIG_FIELD_NAMES_BY_TEST_TYPE,
  WIZARD_FIELDS,
  WIZARD_TARGET_FIELD_NAMES,
  WIZARD_TEST_FIELD_NAMES,
} from './constants';

const MAX_PREVIEW_VALUE_LENGTH = 80;

const WIZARD_NESTED_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  where: 'Where',
  sensitivity: 'Sensitivity',
  trainingPeriod: 'Training period',
  detectionPeriod: 'Detection period',
  failureThreshold: 'Failure threshold',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const clip = (value: string): string =>
  value.length > MAX_PREVIEW_VALUE_LENGTH
    ? `${value.slice(0, MAX_PREVIEW_VALUE_LENGTH).trimEnd()}…`
    : value;

const formatWizardFieldLabel = (field: string): string =>
  WIZARD_NESTED_FIELD_LABELS[field] ??
  capitalizeWords(field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' '));

const formatResource = (value: unknown): string => {
  if (typeof value === 'string') {
    const parts = value.split(/[./]/);
    return parts[parts.length - 1] || value;
  }
  if (isRecord(value)) {
    if (typeof value.name === 'string') {
      return value.name;
    }
    if (typeof value.id === 'string') {
      return formatResource(value.id);
    }
  }
  return clip(JSON.stringify(value));
};

const formatSeverityCondition = (
  prefix: string,
  condition: unknown,
  fallback?: string,
): string | undefined => {
  if (isRecord(condition)) {
    const operator =
      typeof condition.operator === 'string' ? condition.operator : undefined;
    const threshold =
      typeof condition.value === 'number' || typeof condition.value === 'string'
        ? condition.value
        : undefined;
    if (operator !== undefined && threshold !== undefined) {
      return `${prefix} if failures ${operator} ${threshold}`;
    }
  }
  if (typeof fallback === 'string' && fallback.trim()) {
    return `${prefix} if ${fallback}`;
  }
  return undefined;
};

const formatSeverityValue = (
  value: Record<string, unknown>,
): string | undefined => {
  const errorIf =
    typeof value.errorIf === 'string'
      ? value.errorIf
      : typeof value.error_if === 'string'
      ? value.error_if
      : undefined;
  const warnIf =
    typeof value.warnIf === 'string'
      ? value.warnIf
      : typeof value.warn_if === 'string'
      ? value.warn_if
      : undefined;
  const parts = [
    formatSeverityCondition('error', value.conditionalError, errorIf),
    formatSeverityCondition('warn', value.conditionalWarning, warnIf),
  ].filter((part): part is string => !!part);
  if (parts.length > 0) {
    return `conditional, ${parts.join(', ')}`;
  }
  if (
    typeof value.type === 'string' &&
    value.type.toLowerCase() === 'conditional'
  ) {
    return 'conditional';
  }
  if (typeof value.type === 'string') {
    return value.type;
  }
  return undefined;
};

const formatPeriod = (value: Record<string, unknown>): string | undefined => {
  if (typeof value.period !== 'string' || typeof value.count !== 'number') {
    return undefined;
  }
  return pluralizeString(value.count, value.period, `${value.period}s`);
};

const formatWizardFieldValue = (
  field: string,
  value: unknown,
): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    if (field === 'resources' || field === 'owners' || field === 'tags') {
      const list = coerceWizardStringList(value);
      if (list) {
        return formatWizardFieldValue(field, list);
      }
    }
    return clip(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'None';
    }
    if (field === 'resources') {
      const names = value.map(formatResource);
      return names.length <= 3
        ? names.join(', ')
        : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
    }
    if (value.every((item) => typeof item === 'string')) {
      return clip(value.join(', '));
    }
    return pluralizeString(value.length, 'value', 'values');
  }
  if (isRecord(value)) {
    if (field === 'severity') {
      const severity = formatSeverityValue(value);
      if (severity) {
        return severity;
      }
    }
    const period = formatPeriod(value);
    if (period) {
      return period;
    }
    if (typeof value.type === 'string' && Object.keys(value).length <= 2) {
      return value.type;
    }
    const nested = Object.entries(value).flatMap(
      ([nestedField, nestedValue]) => {
        const formatted = formatWizardFieldValue(nestedField, nestedValue);
        return formatted
          ? [`${formatWizardFieldLabel(nestedField)}: ${formatted}`]
          : [];
      },
    );
    return nested.length > 0
      ? clip(nested.join(', '))
      : clip(JSON.stringify(value));
  }
  return String(value);
};

export const previewWizardField = (
  field: string,
  label: string,
  value: unknown,
): PageUpdatePreviewRow[] => [
  { field, label, value: formatWizardFieldValue(field, value) ?? 'None' },
];

const readFormValue = (values: object, field: string): unknown =>
  Object.entries(values).find(([key]) => key === field)?.[1];

type WizardWriteContext = {
  methods: UseFormReturn<AddTestForm>;
  timestampColumns: string[];
  warehouseType?: WarehouseType;
};

const writeWizardField = (
  field: string,
  value: unknown,
  { methods, timestampColumns, warehouseType }: WizardWriteContext,
): FormUiWriteResult => {
  const fieldError = getWizardWriteFieldError(field, value);
  if (fieldError) {
    return { ok: false, reason: fieldError };
  }
  const timestampWrite = normalizeWizardTimestampFieldWrite({
    field,
    value,
    testType: methods.getValues('testType'),
    timestampColumns,
  });
  if (!timestampWrite.ok) {
    return timestampWrite;
  }
  const normalized = normalizeWizardWriteFieldValue(
    field,
    timestampWrite.value,
    readFormValue(methods.getValues(), field),
    warehouseType,
  );
  if (!normalized.ok) {
    return normalized;
  }
  setWizardFormField(methods, field, normalized.value);
  return { ok: true };
};

const writeWizardResources = (
  value: unknown,
  { methods }: WizardWriteContext,
  selectableResourceIds?: string[],
): FormUiWriteResult => {
  const resources = coerceWizardStringList(value);
  if (!resources) {
    return { ok: false, reason: 'resources must be an array of strings' };
  }
  const unselectableReason = selectableResourceIds
    ? getUnselectableWizardResourcesReason(resources, selectableResourceIds)
    : undefined;
  if (unselectableReason) {
    return { ok: false, reason: unselectableReason };
  }
  setWizardFormField(methods, 'resources', resources);
  return { ok: true };
};

const writeWizardTestSelection = (
  proposed: Record<string, unknown>,
  { methods }: WizardWriteContext,
  selectableTests: WizardSelectableTest[],
): FormUiWriteResult => {
  if (selectableTests.length === 0) {
    return { ok: false, reason: 'The test catalog is not available yet' };
  }
  const resolved = resolveWizardTestSelection(proposed, selectableTests);
  if (!resolved.ok) {
    return resolved;
  }
  setWizardFormField(methods, 'testType', resolved.testType);
  setWizardFormField(methods, 'yamlTemplateName', resolved.yamlTemplateName);
  return { ok: true };
};

const writeWizardYaml = (
  value: unknown,
  proposed: Record<string, unknown>,
  context: WizardWriteContext,
): FormUiWriteResult => {
  if (typeof value !== 'string') {
    return { ok: false, reason: 'yaml must be a string' };
  }
  const written = writeWizardField('yaml', value, context);
  if (!written.ok || 'severity' in proposed) {
    return written;
  }
  const liftedSeverity = extractWizardSeverityFromYaml(value);
  if (liftedSeverity) {
    setWizardFormField(context.methods, 'severity', liftedSeverity);
  }
  return written;
};

const isCloudCustomSql = (values: Record<string, unknown>) =>
  values.customQueryDestination === 'cloud';

const WIZARD_FIELD_CONDITIONS: Record<
  string,
  (values: Record<string, unknown>) => boolean
> = {
  baselineColumns: (values) => values.baselineMode === 'manual',
  scheduleType: isCloudCustomSql,
  intervalHours: (values) =>
    isCloudCustomSql(values) && values.scheduleType === 'every_x_hours',
  scheduleHour: (values) =>
    isCloudCustomSql(values) &&
    (values.scheduleType === 'daily' || values.scheduleType === 'weekly'),
  scheduleMinute: (values) =>
    isCloudCustomSql(values) &&
    (values.scheduleType === 'daily' || values.scheduleType === 'weekly'),
  scheduleDayOfWeek: (values) =>
    isCloudCustomSql(values) && values.scheduleType === 'weekly',
  scheduleCron: (values) =>
    isCloudCustomSql(values) && values.scheduleType === 'custom',
  storeRowSample: isCloudCustomSql,
  sampleRowLimit: isCloudCustomSql,
  queryTimeoutSeconds: isCloudCustomSql,
  customSqlPrimaryAssetId: isCloudCustomSql,
  path: (values) => !isCloudCustomSql(values),
  testedTable: (values) => !isCloudCustomSql(values),
};

const isWizardFieldOnStage = (
  field: string,
  values: Record<string, unknown>,
  {
    stage,
    isSingleStep,
    isCustomQuery,
  }: { stage: number; isSingleStep: boolean; isCustomQuery: boolean },
) => {
  if (WIZARD_TARGET_FIELD_NAMES.includes(field)) {
    return isSingleStep ? !isCustomQuery : stage === 1;
  }
  if (WIZARD_TEST_FIELD_NAMES.includes(field)) {
    return isSingleStep ? !isCustomQuery : stage === 2;
  }
  if (!isSingleStep && stage < 3) {
    return false;
  }
  const testType = typeof values.testType === 'string' ? values.testType : '';
  return (
    WIZARD_COMMON_CONFIG_FIELD_NAMES.includes(field) ||
    (WIZARD_CONFIG_FIELD_NAMES_BY_TEST_TYPE[testType] ?? []).includes(field)
  );
};

export const getWizardFormFields = ({
  stage,
  wizardStages,
  wizardMode,
  wizardResourceType,
  selectableTests,
  selectableResourceIds,
  timestampColumns,
  warehouseType,
}: {
  stage: number;
  wizardStages: WizardStageInfo[];
  wizardMode: WizardMode;
  wizardResourceType: WizardResourceType;
  selectableTests: WizardSelectableTest[];
  selectableResourceIds?: string[];
  timestampColumns: string[];
  warehouseType?: WarehouseType;
}): FormUiFields<AddTestForm> => {
  const stageContext = {
    stage,
    isSingleStep: wizardStages.length === 1,
    isCustomQuery: wizardResourceType === 'customQuery',
  };
  return Object.fromEntries(
    Object.entries(WIZARD_FIELDS).map(([field, { label, description }]) => [
      field,
      {
        label,
        ...(description ? { description } : {}),
        isVisible: (values: Record<string, unknown>) =>
          isWizardFieldOnStage(field, values, stageContext) &&
          (WIZARD_FIELD_CONDITIONS[field]?.(values) ?? true),
        lockedReason: (values: Record<string, unknown>) =>
          getWizardFieldLockReason({
            field,
            values,
            wizardMode,
            timestampColumns,
          }),
        onWrite: (
          value: unknown,
          formMethods: UseFormReturn<AddTestForm>,
          proposed: Record<string, unknown>,
        ) => {
          const writeContext = {
            methods: formMethods,
            timestampColumns,
            warehouseType,
          };
          if (field === 'resources') {
            return writeWizardResources(
              value,
              writeContext,
              selectableResourceIds,
            );
          }
          if (WIZARD_TEST_FIELD_NAMES.includes(field)) {
            return writeWizardTestSelection(
              proposed,
              writeContext,
              selectableTests,
            );
          }
          if (field === 'yaml') {
            return writeWizardYaml(value, proposed, writeContext);
          }
          return writeWizardField(field, value, writeContext);
        },
        preview: (value: unknown) => previewWizardField(field, label, value),
      },
    ]),
  );
};

type Rect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export const isRectFullyVisible = (item: Rect, bounds: Rect) =>
  item.top >= bounds.top &&
  item.right <= bounds.right &&
  item.bottom <= bounds.bottom &&
  item.left >= bounds.left;

export const applyGeneratedDescription = (
  methods: {
    getValues: (name: 'description') => string | undefined;
    setValue: (name: 'description', value: string) => void;
  },
  generated: string,
  lastGeneratedRef: { current: string },
) => {
  const current = methods.getValues('description') ?? '';
  const shouldSync = current === '' || current === lastGeneratedRef.current;
  lastGeneratedRef.current = generated;
  if (shouldSync) {
    methods.setValue('description', generated);
  }
};
